<template>
  <div>
    <breadcrumbs
      :breadcrumb-items="breadcrumbItems"
    />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0 && shouldShowToolbar()"
      :account="account"
    />
    <b-card>
      <offer-wall-toolbar />
      <div class="campaigns-table">
        <div class="custom-search">
          <div class="total">
            <strong>{{ $t('Total') }}: {{ totals }}</strong>
          </div>
          <div class="table-filters">
            <div class="d-flex align-items-center">
              <b-input-group class="input-group-merge pnx-search">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="SearchIcon"
                    class="cursor-pointer"
                    @click="search"
                  />
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchTerm"
                  :placeholder="$t('form.search')"
                  type="text"
                  class="d-inline-block"
                  @keyup.enter="search"
                />
                <b-input-group-append is-text>
                  <div class="search-input-clear">
                    <feather-icon
                      v-if="searchTerm!==''"
                      v-b-tooltip.hover.top="$t('form.reset')"
                      class="cursor-pointer"
                      size="18"
                      icon="XIcon"
                      @click="clearSearch"
                    />
                  </div>
                </b-input-group-append>
              </b-input-group>
            </div>
            <div
              v-if="$route.params.userId"
              class="ml-1"
            >
              <b-button
                v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                variant="primary"
                class="btn-icon"
                :to="{ path: `/users/${$route.params.userId}/offerwall/create`}"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                />
                {{ $t('Add') }}
              </b-button>
            </div>
          </div>
        </div>
        <div
          v-if="loading"
          class="d-flex justify-content-center mb-1"
        >
          <b-spinner label="Loading..." />
        </div>
        <vue-good-table
          v-else-if="rows.length !== 0 || totalRecords > 0"
          ref="googleAdsTable"
          mode="remote"
          :columns="columns"
          :rows="rows"
          :rtl="direction"
          :sort-options="{
            enabled: true,
            initialSortBy: {field: 'status', type: 'asc'}
          }"
          :pagination-options="{enabled: true}"
          :total-rows="totalRecords"
          style-class="vgt-table striped"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template
            slot="table-column"
            slot-scope="props"
          >
            <span
              v-if="props.column.label ==='Status'"
              class="text-nowrap"
            >
              {{ $t('campaigns.status') }}
            </span>
            <span
              v-else-if="props.column.label ==='Name'"
              class="text-nowrap"
            >
              {{ $t('campaigns.title') }}
            </span>
            <span
              v-else-if="props.column.label ==='Code'"
              class="text-nowrap"
            >
              {{ 'Ad Code' }}
            </span>
          </template>
          <!-- Slot: Table Row -->
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Column: Status -->
            <span
              v-if="props.column.field === 'title'"
              class="text-nowrap"
            >
              <a
                :href="`/users/${props.row.user_id}/offerwall/${props.row._id}/stats`"
                class="text-primary font-weight-bold"
                @click.prevent="$router.push(`/users/${props.row.user_id}/offerwall/${props.row._id}/stats`)"
              >
                {{ props.row.title }}
              </a>
              <b-badge :variant="status[props.row.status]">
                {{ $t(`selects.statuses.${props.row.status}`) }}
              </b-badge>
              <br>
              <small v-if="props.row.config && props.row.config.gpt && props.row.config.gpt.siteDomain">{{ props.row.config.gpt.siteDomain }}</small>
            </span>
            <span
              v-else-if="props.column.field === 'code'"
              class="text-nowrap"
            >
              <b-button
                v-clipboard:copy="googleAdCode(props.row._id)"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
                v-b-tooltip.hover.top="$t('widgets.copybutton')"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="btn-sm btn-icon"
              >
                <feather-icon icon="CopyIcon" />
              </b-button>
            </span>
            <span
              v-else-if="props.column.field === 'status'"
              class="text-nowrap"
            >
              <b-form-checkbox
                :checked="props.row.status === 'active' ? true : false"
                name="check-button"
                switch
                size="lg"
                @change="changeStatus(props.row._id)"
              />
            </span>
            <span v-else-if="props.column.field === 'action'">
              <span class="d-flex justify-content-center">
                <b-button
                  v-b-tooltip.hover.top="$t('form.change')"
                  :to="'/users/'+ props.row.user_id + '/offerwall/'+ props.row._id + '/edit/'"
                  variant="flat"
                  class="btn-action"
                >
                  <feather-icon
                    icon="EditIcon"
                    class="text-primary"
                    size="16"
                  />
                </b-button>

                <b-button
                  v-if="authUserData.role.includes('admin')"
                  v-b-tooltip.hover.top="$t('form.delete')"
                  variant="flat"
                  class="btn-action"
                  @click="deleteGoogleAd(props.row._id, props.row.title)"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    class="mr-50 text-danger"
                    size="16"
                  />
                </b-button>
              </span>
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap">
                  {{ $t('message.pagelength') }}
                </span>
                <b-form-select
                  v-model="serverParams.page_size"
                  :options="['5','10','20','25','40']"
                  class="mx-1"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap">  {{ $t('message.of') }} {{ props.total }} {{ $t('message.pageText2') }} </span>
              </div>
              <div>
                <b-pagination
                  :value="serverParams.page + 1"
                  :total-rows="props.total"
                  :per-page="parseInt(serverParams.page_size, 10)"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { isOfferWallOperator } from '@/auth/roles'
import useJwt from '@/auth/jwt/useJwt'
import { getUserData } from '@/auth/utils'
import store from '@/store/index'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import {
  BCard,
  BBadge,
  BButton,
  BSpinner,
  BPagination,
  BFormSelect,
  BFormCheckbox,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import OfferWallToolbar from './OfferWallToolbar.vue'
import { apiToForm } from './googleAdsTransform'

function isOfferWallOperatorUser() {
  return isOfferWallOperator(getUserData())
}

export default {
  components: {
    VueGoodTable,
    Breadcrumbs,
    UserToolbar,
    OfferWallToolbar,
    BCard,
    BBadge,
    BButton,
    BSpinner,
    BPagination,
    BFormSelect,
    BFormCheckbox,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BFormInput,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      authUserData: getUserData(),
      account: {},
      loading: false,
      searchTerm: '',
      totalRecords: 0,
      dir: false,
      serverParams: {
        page: 0,
        page_size: '25',
      },
      columns: [
        {
          label: 'Name',
          field: 'title',
          tdClass: 'col-name',
        },
        {
          label: 'Status',
          field: 'status',
          type: 'boolen',
        },
        {
          label: '_id',
          field: '_id',
          hidden: true,
        },
        {
          label: 'user_id',
          field: 'user_id',
          hidden: true,
        },
        {
          label: 'Code',
          field: 'code',
          sortable: false,
          width: '140px',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center',
          tdClass: 'text-center',
          width: '5%',
          sortable: false,
        },
      ],
      breadcrumbItems: [],
      rows: [],
      status: {
        active: 'light-success',
        inactive: 'light-danger',
        is_moderate: 'light-warning',
      },
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        negative     : 'outline-primary',
        active       : 'outline-success',
        inactive     : 'outline-danger',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    totals() {
      return this.totalRecords
    },
  },
  created() {
    if (this.$route.params.userId) {
      this.getUserData()
    } else {
      this.getGoogleAds()
    }
  },
  methods: {
    isOfferWallOperatorUser,
    shouldShowToolbar() {
      if (!isOfferWallOperatorUser()) return true
      const currentUser = getUserData()
      return String(this.$route.params.userId) === String(currentUser?.id)
    },
    search() {
      this.serverParams.page = 0
      this.getGoogleAds()
    },
    clearSearch() {
      this.searchTerm = ''
      this.serverParams.page = 0
      this.getGoogleAds()
    },
    googleAdCode(adId) {
      return `<script src="//phoenix-widget.com/static/js/phx-modal.js?ad_id=${adId}"><${''}/script>`
    },
    onCopy() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('widgets.copybutton') || 'Code copied',
          icon: 'BellIcon',
          variant: 'success',
        },
      })
    },
    onError() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('form.error') || 'Failed to copy code!',
          icon: 'AlertCircleIcon',
          variant: 'danger',
        },
      })
    },
    setBreabcrumbs() {
      if (this.$route.params.userId !== undefined) {
        this.breadcrumbItems = [
          {
            text: 'users',
            to: '/users',
          },
          {
            text: this.account.name,
            to: `/users/${this.$route.params.userId}`,
          },
          {
            text: this.$t('google_ads.list'),
            active: true,
          },
        ]
      } else {
        this.breadcrumbItems = [
          {
            text: this.$t('google_ads.list'),
            active: true,
          },
        ]
      }
    },
    async getUserData() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || []
      this.getGoogleAds()
    },
    async getGoogleAds() {
      this.loading = true
      try {
        const params = {
          page: this.serverParams.page,
          page_size: parseInt(this.serverParams.page_size, 10),
        }
        if (this.$route.params.userId) {
          params.user_id = this.$route.params.userId
        }
        if (this.searchTerm !== '') {
          params.site_domain = this.searchTerm
        }
        const responseData = await useJwt.getAllGoogleWidgets(params)
        const apiData = responseData.data.googlewidgets || []
        // Transform API data to form structure
        this.rows = Object.freeze(
          apiData.map(item => {
            const formData = apiToForm(item)
            return {
              ...formData,
              // Keep original API fields for display
              user_id: item.user_id,
            }
          }),
        )
        // Set total records from API response
        if (responseData.data.total !== undefined) {
          this.totalRecords = responseData.data.total
        } else {
          this.totalRecords = this.rows.length
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('form.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        this.rows = []
        this.totalRecords = 0
      } finally {
        this.loading = false
        this.setBreabcrumbs()
      }
    },
    onPageChange(params) {
      this.serverParams.page = params.currentPage - 1
      this.getGoogleAds()
    },
    onPerPageChange(params) {
      this.serverParams.page_size = params.currentPerPage
      this.serverParams.page = 0
      this.getGoogleAds()
    },
    async changeStatus(id) {
      try {
        const responseData = await useJwt.getGoogleWidget(id)
        const googleWidget = responseData.data.googlewidget || {}
        googleWidget.status = googleWidget.status === 'inactive' ? 'active' : 'inactive'

        if (googleWidget.status === 'active') {
          this.$swal({
            icon: 'success',
            title: 'Activation!',
            text: 'Successfully activated.',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
          await useJwt.updateGoogleWidget(id, { googlewidget: googleWidget })
          this.getGoogleAds()
        } else {
          this.$swal({
            title: 'Confirmation',
            text: 'Will be inactive',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ok',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          }).then(async result => {
            if (result.value) {
              this.$swal({
                icon: 'success',
                title: 'Deactivation!',
                text: 'Successfully deactivated',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
              await useJwt.updateGoogleWidget(id, { googlewidget: googleWidget })
              this.getGoogleAds()
            } else if (result.dismiss === 'cancel') {
              this.$swal({
                title: 'Cancelled',
                text: 'Status remain active',
                icon: 'error',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            }
          })
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('form.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    deleteGoogleAd(id, name) {
      this.$swal({
        title: `${this.$t('swal.deletetitle')} ${name}?`,
        text: this.$t('google_ads.deletemessage'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: this.$t('form.cancel'),
        confirmButtonText: this.$t('form.delete'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          try {
            const deleted = await useJwt.deleteGoogleWidget(id)
            if (deleted.status === 200 || deleted.status === 204) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('swal.successfullydeleted'),
                  icon: 'BellIcon',
                  variant: 'success',
                },
              })
              this.getGoogleAds()
            }
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('form.error'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            })
          }
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

</style>
